import { Vue } from 'vue-property-decorator';
import { PriceDto, PriceTypeDto, ProductBasePrice, ProductNetFillQuantity } from '@eurogast/hub-client-typescript';
import { UnitDefinition } from '@/services/ProductDeserializer';
import { NumberFormatter } from '@/services/NumberFormatter';
import PriceService from '@/services/PriceService';
import { Big } from 'big.js';
import { ConfigService } from '@/services/ConfigService';

export default abstract class ProductComponentBase extends Vue {
  public currentUnit: UnitDefinition = {
    name: '',
    abbreviation: '',
    ratio: 1.0,
    isDefault: true,
    isStorageUnit: true
  };

  // --- start getters for product metadata that should be overridden if that data is available ---

  /**
   * The current amount of this product in the shopping cart. If this product is not in the shopping cart, the
   * amount that will be added when the product is added to the shopping cart.
   */
  abstract get currentAmount(): number;

  /**
   * All bulk prices associated with this product, not filtered by being applicable to the currently selected amount.
   */
  abstract get bulkPrices(): PriceDto[];

  /**
   * The list of units available for this product, including invisible units.
   */
  abstract get units(): UnitDefinition[];

  /**
   * The base price definition for this product, if available, otherwise null.
   */
  abstract get basePrice(): ProductBasePrice;

  /**
   * The net filling quantity for this product, if available, otherwise null.
   */
  abstract get netFillQuantity(): ProductNetFillQuantity;

  /**
   * The SKU of this product.
   */
  abstract get sku(): string;

  /**
   * Whether a current amount of zero is allowed for this product in the current context.
   */
  get isZeroAmountAllowed(): boolean {
    return false;
  }

  get showSkuInSubTitle(): boolean {
    return true;
  }

  // --- end getters for product metadata that should be overridden if that data is available ---

  public mounted() {
    if (this.currentUnit.abbreviation === '') {
      this.currentUnit = this.defaultUnit;
    }
  }

  /**
   * The default unit currently defined for this product. If no units are associated with the current product,
   * behaviour is undefined.
   */
  get defaultUnit(): UnitDefinition {
    if (!this.units) {
      return null;
    } else {
      return this.units.find((unit) => unit.isDefault);
    }
  }

  /**
   * The storage unit currently defined for this product. If no units are associated with the current product,
   * behaviour is undefined.
   */
  get storageUnit(): UnitDefinition {
    if (!this.units) {
      return null;
    } else {
      return this.units.find((unit) => unit.isStorageUnit);
    }
  }

  /**
   * Returns the subset of units that are marked as visible.
   */
  get visibleUnits(): UnitDefinition[] {
    if (!this.units) {
      return null;
    } else {
      return this.units.filter((unit) => unit.isVisible);
    }
  }

  /**
   * Finds the bulk price that is currently the cheapest applicable one, taking in account the minimum quantity
   * of prices.
   */
  get currentBulkPrice(): PriceDto {
    if (!this.bulkPrices || this.bulkPrices.length === 0) {
      return null;
    }
    // For order templates w/o amount, always pick the first bulk price, regardless of quantity set in price
    if (this.isZeroAmountAllowed && this.currentAmount === 0) {
      return this.bulkPrices[0];
    }
    return PriceService.findBestBulkPrice(this.bulkPrices, this.currentAmount, this.currentUnit);
  }

  /**
   * The current total price for the current amount, taking into account the current bulk price and unit.
   */
  get totalPrice(): Big {
    return PriceService.findFinalPrice(this.currentBulkPrice, this.currentAmount, this.currentUnit);
  }

  /**
   * A human-readable formatted string describing the current total price.
   */
  get totalPriceString(): string {
    const formatted = NumberFormatter.formatPrice(this.totalPrice);
    return formatted ? formatted + '*' : '';
  }

  /**
   * A human-readable formatted string describing the current price of a single unit of the storage unit of this
   * product, taking into account the current bulk price, and showing the abbreviation of the storage unit.
   */
  get storagePriceString(): string {
    if (!this.hasPrice) {
      return '';
    }
    return NumberFormatter.formatStoragePrice(this.currentBulkPrice, this.storageUnit);
  }

  /**
   * A human readable formatted string describing the chosen amount in base units
   */
  get storageAmountString(): string {
    const ratio = this.currentUnit ? this.currentUnit.ratio : 1;
    const scaledAmount = new Big(this.currentAmount).mul(ratio);
    return NumberFormatter.formatStorageAmount(scaledAmount, this.storageUnit);
  }

  /**
   * Builds a string describing the base price of this product relative to the base price reference quantity.
   * This uses the current bulk price as reference and includes the description of the base price reference unit.
   * Returns an empty string if there is no price or the base price information is incomplete.
   */
  get basePriceString(): string {
    if (!this.hasPrice) {
      return '';
    }
    return NumberFormatter.formatBasePriceString(this.basePrice, this.currentBulkPrice, this.storageUnit);
  }

  /**
   * Builds a string representing the net filling quantity for this product and its unit.
   */
  get netFillString(): string {
    if (!this.netFillQuantity || !this.netFillQuantity.unit) {
      return '';
    } else {
      const formatted = NumberFormatter.formatFillQuantity(this.netFillQuantity.quantity);
      return formatted + this.netFillQuantity.unit;
    }
  }

  /**
   * Builds a string representing the rrp/uvp for this product
   */
  get uvpString(): string {
    return NumberFormatter.formatUVPPriceString(this.storageUnit, this.currentBulkPrice);
  }

  /**
   * Returns human-readable information that is to be displayed alongside the product name. This includes the
   * net filling quantity, the base price, and the SKU of this product. Any unavailable information is omitted.
   */
  get subTitle(): string {
    const netFillString = this.netFillString;
    const basePriceString = this.basePriceString;
    const uvpPriceString = this.uvpString;
    let result = '';
    if (netFillString && basePriceString) {
      result = `${netFillString} (${basePriceString})`;
    } else if (netFillString && !basePriceString) {
      result = netFillString;
    } else if (!netFillString && basePriceString) {
      result = `(${basePriceString})`;
    }
    if (uvpPriceString) {
      if (result !== '') {
        result += ' - ';
      }
      result += this.$t('common.product.withUvp', {uvp: uvpPriceString}).toString();
    }
    if (this.showSkuInSubTitle) {
      if (result !== '') {
        result += ' - ';
      }
      result += this.$t('common.product.withSku', {sku: this.sku}).toString();
    }
    return result;
  }

  /**
   * The type of the currently applicable price, or an empty string if there is none.
   */
  get priceType(): PriceTypeDto | string {
    if (this.currentBulkPrice) {
      return this.currentBulkPrice.type;
    } else {
      return '';
    }
  }

  /**
   * Describes the amount and unit currently in the cart.
   */
  get cartDescriptor(): string {
    if (this.currentUnit && this.currentUnit.abbreviation) {
      return `${this.currentAmount} ${this.currentUnit.abbreviation}`;
    } else {
      return '';
    }
  }

  /**
   * Whether the current user is logged in, bulk prices are available, there is a current bulk price, and that price
   * has a value associated.
   */
  get hasPrice(): boolean {
    return !!(this.isLoggedIn && this.currentBulkPrice && this.currentBulkPrice.price);
  }

  /**
   * Whether this product has a price and there is more than one bulk price.
   */
  get hasBulkPricing(): boolean {
    return this.hasPrice && this.bulkPrices.length > 1;
  }

  get isLoggedIn(): boolean {
    return ConfigService.isLoggedIn();
  }

  public isValidAmount(value) {
    return !(value < 1 || isNaN(Number(value)) || value.toString() === '');
  }
}
